import React from 'react';
import { motion } from 'framer-motion';
import { Gift } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
import { ScrollTable } from './ScrollTable';
const bonuses = [{
  id: '01',
  title: 'Rider Confidence & Instructor Communication Toolkit',
  value: '12',
  desc: '100 ready-to-use communication scripts for every teaching situation — from welcoming a new student to correcting position without discouraging, from supporting a nervous rider to closing the session with purpose.',
  covers: 'Covers: welcoming the rider, explaining safety, presenting new skills, correcting without demotivating, brief instructions, nervous riders, reducing confusion, reinforcing improvement, position corrections, trot guidance, pole preparation, communicating limits, stopping an activity, reformulating explanations, parent communication, session objectives, session closing, progress summaries, and next-step explanations.',
  benefit: 'No preparation needed. Open it, read it, teach.'
}, {
  id: '02',
  title: 'Riding School Forms, Records & Organization Toolkit',
  value: '12',
  desc: '60 templates and content guides for professional organization — so you spend your preparation time on your teaching, not on remembering what happened in the last session.',
  covers: 'Covers: rider registration, level records, individual objectives, skills history, progress tracking, post-lesson notes, weekly and monthly planning, horse assignment, session observations, attendance, equipment records, preparation checklists, family communication, incident reports, progression criteria, skills assessment, group planning, and pending tasks.',
  benefit: 'A professional organization system — ready to use wherever you teach.'
}, {
  id: '03',
  title: '100 Riding Games, Arena Challenges & Pole Layouts',
  value: '12',
  desc: '100 structured activities in 7 categories so your lessons stay dynamic and purposeful — without repeating the same exercises week after week.',
  covers: '20 balance activities · 20 direction activities · 15 confidence-building games · 15 transition exercises · 15 ground-pole layouts · 10 group challenges · 5 ground-based activities',
  benefit: 'Never teach the same lesson twice. The variety is already built in.'
}, {
  id: '04',
  title: 'Mixed-Level, Children & Adult Lesson Adaptation System',
  value: '12',
  desc: 'The complete system for adapting the same lesson structure to children, teenagers, adult beginners, returning riders, nervous riders, fast-progressing students, and mixed-level groups — without creating a completely different lesson for each profile.',
  covers: 'Includes modifications for language, explanation length, instruction quantity, difficulty level, figure size, exercise duration, pole use, repetitions, session rhythm and progression criteria.',
  benefit: 'Three versions of every skill. Ready before the lesson begins.'
}, {
  id: '05',
  title: 'Emergency Lesson & Bad Weather Vault',
  value: '10',
  desc: '50 complete alternative lesson plans for the days when your original plan cannot happen — bad weather, unavailable arena, horse change, unmounted sessions, mixed-level groups, or any unexpected situation.',
  covers: '',
  benefit: 'Never walk into a lesson without a plan. 50 alternatives — ready when you need them.'
}];
const bonusValueRows = [['Bonus 1 — Communication Toolkit', '$12'], ['Bonus 2 — Forms & Organization Toolkit', '$12'], ['Bonus 3 — Games, Challenges & Pole Layouts', '$12'], ['Bonus 4 — Adaptation System', '$12'], ['Bonus 5 — Emergency Lesson Vault', '$10']];
export function BonusSection() {
  return <section className="py-24 px-4 bg-ivory" data-id="element-16" data-mp="e8yhox0hkeyz">
      <div className="max-w-5xl mx-auto" data-id="element-17" data-mp="2u1fjtoyt8oxt">
        <div className="text-center mb-16" data-id="element-18" data-mp="2u0m96cwqdfae">
          <p className="text-gold-aged font-bold tracking-widest uppercase mb-2" data-id="element-19" data-mp="2z0s8mf98s631">
            Included at no extra cost
          </p>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-forest" data-id="element-20" data-mp="3f8yd6jyl9tbe">
            🎁 The 5 Strategic Bonuses
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-21" data-mp="35id585x0zcft">
          {bonuses.map((bonus, index) => <motion.div key={bonus.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-white border border-gold-aged/40 rounded-2xl p-8 relative hover:border-gold transition-colors shadow-[0_4px_20px_rgba(18,53,36,0.06)] flex flex-col" data-id="element-22" data-mp="30jad1m2590p9" data-mpi={index}>
              <div className="absolute top-0 right-0 bg-forest text-gold px-4 py-2 rounded-bl-2xl rounded-tr-2xl font-bold text-sm" data-id="element-23" data-mp="3jajnpynnhvif">
                BONUS {bonus.id}
              </div>

              <div className="w-12 h-12 bg-leather rounded-full flex items-center justify-center text-forest mb-6" data-id="element-24" data-mp="27u3c6gf7vz0u">
                <Gift size={24} data-id="element-25" data-mp="ndlp596plasw" />
              </div>

              <h3 className="text-xl font-serif font-bold mb-2 pr-20 text-forest" data-id="element-26" data-mp="napv3vxzf69q">
                {bonus.title}
              </h3>

              <div className="inline-block self-start bg-cream border border-gold/40 px-3 py-1 rounded mb-4" data-id="element-27" data-mp="xagmvpv5r764">
                <span className="text-warmgray text-sm" data-id="element-28" data-mp="122i6jwuno066">
                  Real value:{' '}
                  <span className="line-through" data-id="element-29" data-mp="171bdt7jcmuxw">${bonus.value}</span>
                </span>
                <span className="text-forest font-bold ml-2" data-id="element-30" data-mp="1b1dfhgp33od6">→ FREE</span>
              </div>

              <p className="text-ink text-sm mb-4 leading-relaxed" data-id="element-31" data-mp="1g0q343ns4zgt">
                {bonus.desc}
              </p>

              {bonus.covers && <p className="text-warmgray text-sm mb-6 leading-relaxed flex-grow" data-id="element-32" data-mp="1kzjcpkzy46bp">
                  {bonus.covers}
                </p>}

              <p className="text-pine text-sm italic font-medium mt-auto" data-id="element-33" data-mp="3mqw7vic42zuk">
                {bonus.benefit}
              </p>
            </motion.div>)}
        </div>

        <div className="max-w-2xl mx-auto mb-8" data-id="element-34" data-mp="3apo585bsie95">
          <h3 className="text-center text-xl font-serif font-bold text-forest mb-4" data-id="element-35" data-mp="3aqz90558jm09">
            Total value of the 5 bonuses
          </h3>
          <ScrollTable data-id="element-36" data-mp="3jq2s394ek1er">
            <thead data-id="element-37" data-mp="3jpjj4hwqcncv">
              <tr className="bg-forest-dark text-ivory" data-id="element-38" data-mp="3jpb7sug99q3s">
                <th scope="col" className="px-4 py-3 font-bold" data-id="element-39" data-mp="1k7z6zr2yvym">
                  Bonus
                </th>
                <th scope="col" className="px-4 py-3 font-bold text-right" data-id="element-40" data-mp="3toscp9nonzyp">
                  Value
                </th>
              </tr>
            </thead>
            <tbody data-id="element-41" data-mp="bcdpm51op5bp">
              {bonusValueRows.map(([name, value], i) => <tr key={name} className={i % 2 === 0 ? 'bg-white' : 'bg-cream/70'} data-id="element-42" data-mp="6bq1br4ub2l6" data-mpi={i}>
                  <th scope="row" className="px-4 py-3 font-medium text-ink" data-id="element-43" data-mp="l9ib02npgwrg">
                    {name}
                  </th>
                  <td className="px-4 py-3 text-right text-warmgray" data-id="element-44" data-mp="gb71jt1boo9r">{value}</td>
                </tr>)}
              <tr className="bg-forest text-ivory" data-id="element-45" data-mp="ztbg2q5bj3e5">
                <th scope="row" className="px-4 py-3 font-bold" data-id="element-46" data-mp="13vg51bktnbug">
                  Total bonus value
                </th>
                <td className="px-4 py-3 text-right font-bold text-gold" data-id="element-47" data-mp="pwq8bx1wze6m">
                  $58.00
                </td>
              </tr>
            </tbody>
          </ScrollTable>
        </div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="bg-leather border border-gold rounded-2xl p-8 text-center max-w-2xl mx-auto" data-id="element-48" data-mp="g5lq8ogxzd7k">
          <p className="text-lg text-ink mb-2" data-id="element-49" data-mp="c4s5u52pkgjf">Your price today</p>
          <p className="text-5xl font-bold text-forest mb-4" data-id="element-50" data-mp="c37e0n7s3bp9">🎁 FREE</p>
          <p className="text-sm text-warmgray" data-id="element-51" data-mp="3tegrvt22pkgn">
            Only included in this offer.
          </p>
        </motion.div>

        <CountdownTimer data-id="element-52" data-mp="298b3lzvrnw9" />
      </div>
    </section>;
}